/**
 * Budget-Auslastung für den Statusbericht.
 *
 * Aus der Komponente herausgezogen, damit die Rechnung ohne Browser prüfbar
 * ist — sie steht im **Kundenbericht** und ist die Grundlage der
 * Budgetdiskussion mit dem Kunden.
 *
 * Der Kern der Trennung (Befund W6): Gekappt wird nur die **Darstellung** des
 * Balkens, nie die **Zahl**. Zuvor gab `fill` — der auf 0–100 begrenzte
 * Balkenwert — auch die Prozentangabe ab; ein um 200 % überzogenes Budget las
 * sich damit als „100 % verbraucht", also als punktgenau ausgeschöpft.
 */

export interface BudgetShare {
  /** Vertragsvolumen. `null` oder 0 heißt: keine Bezugsgröße. */
  budgetEur: number | null;
  /** Bereits abgerufen. */
  billedEur: number | null;
}

/**
 * Verbrauch in Prozent — **ungekappt**. Ohne Vertragsvolumen gibt es keine
 * Bezugsgröße; dann 0, damit die Ampel nicht auf einer Division durch null
 * rechnet.
 */
export function budgetUsedPct(budget: BudgetShare): number {
  if (!budget.budgetEur) return 0;
  return ((budget.billedEur ?? 0) / budget.budgetEur) * 100;
}

/**
 * Anteil des Balkens in Prozent, auf 0–100 begrenzt. Ein Balken kann nicht
 * über seine Breite hinauswachsen — die Zahl daneben sehr wohl.
 */
export function budgetBarFill(usedPct: number): number {
  return Math.round(Math.min(Math.max(usedPct, 0), 100));
}

/**
 * Rolle eines Balkenabschnitts. Die Farbe hängt daran, nicht umgekehrt — der
 * Bericht entscheidet über die Farbe, diese Datei über die Bedeutung.
 */
export type BudgetBarKind = "used" | "remaining" | "overrun";

export interface BudgetBarSegment {
  kind: BudgetBarKind;
  /** Anteil an der Gesamtbreite in Prozent; die Summe ist immer genau 100. */
  widthPct: number;
}

/**
 * Abschnitte des Fortschrittsbalkens (Befund B2).
 *
 * Zuvor bekam die **farbige** Zelle die Breite des Rests und die graue die des
 * Verbrauchs — der Balken zeigte also exakt das Gegenteil der Zahl daneben
 * („79 % verbraucht" bei 21 % Füllung, gemessen an drei Beauftragungen).
 *
 * Der überzogene Fall war der schlimmere: `fill` wird bei über 100 % auf 100
 * gekappt, der Rest damit auf 0 — es blieb **eine graue Zelle mit 100 %
 * Breite**, also der leerste aller Balken für das am weitesten überzogene
 * Budget. Betroffen sind 19 von 117 Beauftragungen mit Budgetblock
 * (Stand 06.08.2026), Extremwert 290,7 %.
 *
 * Entscheidung für den überzogenen Fall: Der Balken ist **immer voll** — es ist
 * nichts mehr übrig, eine graue Spur wäre gelogen. Ein bloß voller Warnbalken
 * sähe allerdings bei 101 % genauso aus wie bei 291 %. Deshalb wird die
 * Bezugsgröße dort vom Vertragsvolumen auf den **Verbrauch** umgestellt: Der
 * Anteil, der noch ins Budget passte, steht in der Ampelfarbe, der Teil
 * jenseits des Volumens in einem dunkleren Ton derselben Familie. Bei 113 %
 * ist das ein schmaler dunkler Streifen, bei 291 % zwei Drittel des Balkens —
 * das Ausmaß der Überziehung ist damit auf einen Blick ablesbar, und ein
 * punktgenau ausgeschöpftes Budget (voll, ohne dunklen Teil) bleibt
 * unterscheidbar.
 *
 * Bewusst reine Prozentbreiten: Der Bericht geht als verschachtelte Tabelle
 * mit `bgcolor` und `width="x%"` nach Outlook — Verläufe, Muster oder
 * Randstriche wären dort nicht verlässlich.
 */
export function budgetBarSegments(usedPct: number): BudgetBarSegment[] {
  const drop = (segments: BudgetBarSegment[]) =>
    segments.filter((s) => s.widthPct > 0);

  if (usedPct > 100) {
    // Anteil des Balkens, der noch ins Volumen passte. 100/usedPct, weil die
    // volle Breite jetzt den Verbrauch abbildet und nicht das Volumen.
    const within = Math.round((100 / usedPct) * 100);
    return drop([
      { kind: "used", widthPct: within },
      { kind: "overrun", widthPct: 100 - within },
    ]);
  }

  const used = budgetBarFill(usedPct);
  return drop([
    { kind: "used", widthPct: used },
    { kind: "remaining", widthPct: 100 - used },
  ]);
}

/**
 * Prozentangabe für den Bericht: der **echte** Wert, kaufmännisch gerundet.
 * Bewusst ein gewöhnliches Leerzeichen vor dem Prozentzeichen — der Text geht
 * über die Zwischenablage nach Outlook, und dort sind Sonderformen des
 * Leerzeichens schon als Kästchen angekommen.
 */
export function budgetPercentText(budget: BudgetShare): string {
  return `${Math.round(budgetUsedPct(budget))} %`;
}
